import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import ConfirmDialog from '../../components/ConfirmDialog'
import ExcelImportModal from '../../components/ExcelImportModal'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import IconButton from '../../components/IconButton'
import PageHeader from '../../components/PageHeader'
import StatusBadge from '../../components/StatusBadge'
import { excel as EXCEL } from '../../components/strings'
import { useToast } from '../../components/toastContext'
import {
  deleteTemplate,
  exportTemplates,
  importTemplates,
  listTemplates,
} from './phishingService'
import { templatesStrings as S } from './strings'
import { TemplatePreview, TemplateShot } from './TemplateSurfaces'
import './phishing.css'
import { localeCode, serverText } from '../../lib/i18n'
import { PHISHING_PAYLOAD_TYPE } from '../../domain/domainCodes'

/**
 * WHAT EACH SORTABLE COLUMN ORDERS BY.
 *
 * The UNDERLYING value, not the label the cell draws: level and content are stored as
 * their number, so these read easy-to-hard and by payload rank rather than alphabetically
 * by a word that differs between Arabic and English. Only these four are sortable; the
 * three surface previews and the actions column set `sortable: false`.
 */
const SORT_VALUE = {
  name: (row) => row.name ?? '',
  isArabic: (row) => (row.isArabic ? 1 : 0),
  difficulty: (row) => row.difficulty ?? 0,
  payloadType: (row) => row.payloadType ?? PHISHING_PAYLOAD_TYPE.WEBSITE,
}

/** Harder templates read as more severe, so the badge tone tracks the level. */
const DIFFICULTY_TONE = { 0: 'success', 1: 'warning', 2: 'danger' }

/** The id the server would mint; the mock keeps the list's own sequence. */

/** قوالب الإصطياد — Views/PhishingTemplates/Index.cshtml. */
export default function TemplatesPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [query, setQuery] = useRemembered('phishing-templates:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one — without this the table renders its "no
     records" state for a server error, which is the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [importing, setImporting] = useState(false)
  /** Which template's surfaces are open, and which of the three is showing. */
  const [preview, setPreview] = useState(null)

  const openPreview = useCallback(
    (template) => (surface) => setPreview({ template, surface }),
    [],
  )

  /* SERVER-PAGED. The API pages and searches this list — `length` is clamped to 100 and the
     search runs on the name — so the table must not also filter locally or it would hide
     rows the server deliberately sent. */
  /* In the address, so Back and a refresh both land on the same page. */
  const [page, setPage] = usePageParam()
  /* TEN, not the usual twenty-five. Every row carries its three HTML surfaces double-base64
     and `/filter` has no way to ask for fewer fields, so a page of 25 is ~5.6 MB and a page
     of 100 is 45 MB. The reader can still choose a larger page; the default should not. */
  const [pageSize, setPageSize] = useRemembered('phishing-templates:pageSize', 10)
  const [total, setTotal] = useState(0)
  const [reloadKey, setReloadKey] = useState(0)
  /* Matches the arrow DataTable draws before anything is clicked - its first sortable
     column, ascending - so the header and the rows agree from the first paint. */
  const [sort, setSort] = useRemembered('phishing-templates:sort', { key: 'name', dir: 'asc' })

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listTemplates({ page, pageSize, query })
      .then((response) => {
        if (cancelled) return
        setLoadError(null)
        setRows(response.rows)
        setTotal(response.total)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setTotal(0)
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [page, pageSize, query, reloadKey, toast])

  const remove = async () => {
    const template = pendingDelete
    setPendingDelete(null)
    const outcome = await deleteTemplate(template.id)
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.deleted)
    setReloadKey((key) => key + 1)
  }

  /* One template with an id, or the whole library without one — the same route either way.
     Memoised because the column definitions close over it. */
  const exportZip = useCallback(
    async (id) => {
      const outcome = await exportTemplates(id)
      if (!outcome.ok) toast.error(outcome.message || S.actionFailed)
    },
    [toast],
  )

  const runImport = async (file) => {
    setImporting(false)
    const outcome = await importTemplates(file)
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.imported)
    setReloadKey((key) => key + 1)
  }

  /*
   * SORTS THE PAGE, NOT THE LIBRARY.
   *
   * `/filter` orders by last update and pages BEFORE it orders, and it ignores the
   * `order[...]` fields entirely - so the rows on screen are one page of a newest-first
   * list and no request this screen can make will change which rows those are. Reordering
   * them here is therefore exactly what it appears to be: the rows in front of the reader,
   * put in order. Page 2 still holds whatever the server sent for page 2.
   *
   * That is why `DataTable` refuses to sort in server mode and why this is done here
   * instead: the table cannot know the difference, but this screen can, and it says so
   * under the search box whenever the list runs past one page.
   */
  const sortedRows = useMemo(() => {
    const read = SORT_VALUE[sort?.key]
    if (!read) return rows

    const direction = sort.dir === 'desc' ? -1 : 1
    return [...rows].sort((first, second) => {
      const left = read(first)
      const right = read(second)
      // Arabic names do not order correctly under a plain `<`.
      if (typeof left === 'string') return left.localeCompare(right, localeCode) * direction
      return (left - right) * direction
    })
  }, [rows, sort])

  const columns = useMemo(
    () => [
      /* The name carries no tile any more. It used to lead with a drawn window
         standing in for a screenshot the record does not hold — and now that
         the three real surfaces have columns of their own, that tile was a
         fourth, vaguer preview competing with them. */
      {
        key: 'name',
        header: S.columns.name,
        /* Pinned. Three preview columns plus the four text ones leave the name
           whatever is left over, and left over at 1440 was six words stacked
           one per line. The table scrolls instead — a name that has to be read
           beats a table that has to fit. */
        width: '230px',
        render: (row) => <bdi>{row.name}</bdi>,
      },
      {
        key: 'isArabic',
        header: S.columns.language,
        value: (row) => (row.isArabic ? S.languages.ar : S.languages.en),
        render: (row) => (
          <StatusBadge tone="primary" plain>
            {row.isArabic ? S.languages.ar : S.languages.en}
          </StatusBadge>
        ),
      },
      {
        key: 'difficulty',
        header: S.columns.difficulty,
        value: (row) => S.difficulties[row.difficulty] ?? '',
        render: (row) => (
          <StatusBadge tone={DIFFICULTY_TONE[row.difficulty] ?? 'dark'}>
            {S.difficulties[row.difficulty] ?? ''}
          </StatusBadge>
        ),
      },
      {
        key: 'payloadType',
        header: S.columns.payloadType,
        value: (row) => S.payloads[row.payloadType] ?? '',
        render: (row) => <bdi>{S.payloads[row.payloadType] ?? ''}</bdi>,
      },
      /* The three stored screenshots, in the legacy's own order: what lands in
         the inbox, what it opens, what the target is shown afterwards. */
      ...[
        ['emailImage', 'email'],
        ['landingImage', 'landing'],
        ['afterImage', 'after'],
      ].map(([key, surface]) => ({
        key,
        header: S.columns[key],
        align: 'center',
        width: '136px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <TemplateShot template={row} surface={surface} onOpen={openPreview(row)} />
        ),
      })),
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '140px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="data-table__row-actions">
            <IconButton
              icon={Download}
              label={S.export}
              /* Utility, not an affirmation. Green is what activating something looks
                 like in this panel; downloading a copy is neither good nor bad news. */
              tone="primary"
              onClick={() => exportZip(row.id)}
            />
            {/* THE ROW TRAVELS WITH THE CLICK. `/filter` is the only route that returns a
                template, it has no field projection, and every row carries three
                double-base64 HTML surfaces — so re-fetching one costs megabytes for data
                this page is already holding. The editor falls back to a fetch only when it
                is opened cold, from a bookmark or a refresh. */}
            <IconButton
              icon={Pencil}
              label={S.edit}
              onClick={() => navigate(`/phishing/templates/${row.id}/edit`, { state: { template: row } })}
            />
            <IconButton
              icon={Trash2}
              label={S.delete}
              tone="danger"
              onClick={() => setPendingDelete(row)}
            />
          </span>
        ),
      },
    ],
    [openPreview, navigate, exportZip],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.editor}
        count={loading ? undefined : total}
        actions={
          <>
            <Button onClick={() => navigate('/phishing/templates/new')}>
              <Plus size={16} strokeWidth={2} />
              {S.addNew}
            </Button>
            <Button variant="soft" onClick={() => setImporting(true)}>
              <Upload size={16} strokeWidth={1.75} />
              {S.import}
            </Button>
            <Button variant="soft" onClick={() => exportZip(null)}>
              <Download size={16} strokeWidth={1.75} />
              {S.export}
            </Button>
          </>
        }
      />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.name }} />

      {/* Only when it is actually true. On a library that fits in one page the sort IS the
          whole list, and a caveat there would be noise. */}
      {total > rows.length && <p className="ph__sort-scope">{S.sortScope}</p>}

      <DataTable
        error={loadError}
        columns={columns}
        rows={sortedRows}
        loading={loading}
        query={query}
        getRowId={(row) => row.id}
        server={{
          page,
          pageSize,
          total,
          onChange: (next) => {
            const reordered = next.sort?.key !== sort?.key || next.sort?.dir !== sort?.dir
            setSort(next.sort)
            setPageSize(next.pageSize)
            /* A sort does NOT return to page one. DataTable asks for it because a
               server-side sort means a different first page - but this one reorders the
               page already on screen, so obeying would throw the reader off the very rows
               they were putting in order. */
            if (!reordered) setPage(next.page)
          },
        }}
      />

      <TemplatePreview
        template={preview?.template ?? null}
        surface={preview?.surface}
        onSurfaceChange={(surface) => setPreview((current) => ({ ...current, surface }))}
        onClose={() => setPreview(null)}
      />


      {/* `استيراد` rendered and did nothing. */}
      {/* A ZIP, NOT A SPREADSHEET. The importer reads `{id}/details.json` out of a zip
          archive and refuses any other extension; the modal offered `.xls,.xlsx` and linked
          to an Excel template that does not exist. There is no downloadable template for
          this format — the archive comes from this screen's own export. */}
      <ExcelImportModal
        open={importing}
        title={S.import}
        accept=".zip"
        /* Both overridden: the shared note tells the reader to download a template first
           and keep its column order, which is Excel advice and false here — there is no
           downloadable template for this format, and it has no columns. */
        strings={{ intro: EXCEL.introZip, notes: S.importNote }}
        onClose={() => setImporting(false)}
        onSubmit={runImport}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.delete}
        cancelLabel={S.close}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
